import {Component, OnDestroy, OnInit} from '@angular/core';
import {Update} from '@ngrx/entity';
import {Observable, Subscription} from 'rxjs';
import {ActivatedRoute, Router} from '@angular/router';
import {SubheaderService} from '../../../../core/_base/layout';
import {AppState} from '../../../../core/reducers';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {select, Store} from '@ngrx/store';
import {delay} from 'rxjs/operators';
import {WindowTypeUpdated} from '../../../../core/linked-tables';
import { TranslateService } from '@ngx-translate/core';

import {
    WindowType,
    WindowTypeOnServerCreated,
    selectWindowTypesActionLoading,
    selectWindowTypeById,
    selectLastCreatedWindowTypeId
} from '../../../../core/linked-tables';
import {Location} from '@angular/common';
import {combineLatest} from 'rxjs'

@Component({
    selector: 'kt-window-type-edit',
    templateUrl: './window-type-edit.component.html',
    styleUrls: ['./window-type-edit.component.scss'],
})


export class WindowTypeEditComponent implements OnInit, OnDestroy {

    model: WindowType;
    oldModel: WindowType;

    windowTypeForm: UntypedFormGroup;
    hasFormErrors = false;

    loading$: Observable<boolean>;
    viewLoading = false;
    loadingAfterSubmit = false;
    mode = 'NEW';
    readonly = false
    // Private properties
    private componentSubscriptions: Subscription;
    private subscriptions: Subscription[] = [];

    /**
     * Component constructor
     *
     * @param activatedRoute
     * @param router
     * @param fb: FormBuilder
     * @param location
     * @param subheaderService
     * @param store: Store<AppState>
     */
    constructor(private activatedRoute: ActivatedRoute,
                private router: Router,
                private fb: UntypedFormBuilder,
                private location: Location,
                public subheaderService: SubheaderService,
                private store: Store<AppState>,
                private translate: TranslateService) {
    }

    ngOnInit() {
        this.loading$ = this.store.pipe(select(selectWindowTypesActionLoading));
        const routeSubscription = combineLatest([
            this.activatedRoute.params,
            this.activatedRoute.data
        ]).subscribe(([params, data]) => {
            const id = params.id;
            this.readonly = data.readonly
            if (id && id > 0) {
                this.store.pipe(select(selectWindowTypeById(id))).subscribe(res => {
                    if (res) {
                        this.oldModel = Object.assign({}, res);
                        this.model = Object.assign({}, this.oldModel);
                        this.initWindowType();
                    }
                });
            } else {
                this.model = new WindowType();
                this.model.clear();
                this.oldModel = Object.assign({}, this.model);
                this.initWindowType();
            }
        });
        this.subscriptions.push(routeSubscription);

        if (this.activatedRoute.snapshot.paramMap.get('id') != null) {
            this.setBreadCrumbs(Number(this.activatedRoute.snapshot.paramMap.get('id')));
        } else {
            this.setBreadCrumbs(0);
        }
    }

    setBreadCrumbs(id: number): void {
        this.subheaderService.setTitle(this.getComponentTitle());
        this.subheaderService.setBreadcrumbs([
            {title: 'Developer', page: `../default/admin-management`},
            {title: 'Linked tables', page: `../default/admin-management/linked-tables`},
            {title: 'Window Types', page: `../default/admin-management/linked-tables/base-of-values`},
            {
                title: this.getComponentTitle(),
                page: id > 0 ? `../default/admin-management/linked-tables/base-of-values/edit/${id}`
                    : `../default/admin-management/linked-tables/base-of-values/add`
            }
        ]);
    }

    ngOnDestroy(): void {
        if (this.componentSubscriptions) {
            this.componentSubscriptions.unsubscribe();
        }
    }

    initWindowType() {
        this.createForm();
    }


    createForm() {
        this.windowTypeForm = this.fb.group({
            name: [this.model.name, Validators.required],
        });
    }

    /**
     * Returns page title
     */
    getComponentTitle(): string {
        if (this.model && this.model.id > 0) {
            this.mode = 'EDIT';
            return `Edit Window Type '${
                this.model.name
                }'`;
        }

        return 'New Window Type';
    }

    /**
     * Check control is invalid
     * @param controlName: string
     */
    isControlInvalid(controlName: string): boolean {
        const control = this.windowTypeForm.controls[controlName];
        return control.invalid && control.touched;
    }

    reset() {
        this.model = Object.assign({}, this.oldModel);
        this.createForm();
        this.hasFormErrors = false;
        this.windowTypeForm.markAsPristine();
        this.windowTypeForm.markAsUntouched();
        this.windowTypeForm.updateValueAndValidity();
    }

    /** ACTIONS */

    /**
     * On Submit
     */
    onSubmit(withBack: boolean = false) {
        this.hasFormErrors = false;
        const controls = this.windowTypeForm.controls;
        /** check form */
        if (this.windowTypeForm.invalid) {
            Object.keys(controls).forEach(controlName =>
                controls[controlName].markAsTouched()
            );

            this.hasFormErrors = true;
            return;
        }

        if (this.model.id > 0) {
            this.updateWindowType(this.model);
        } else {
            this.createWindowType(this.model);
        }
    }

    /**
     * Update model
     *
     * @param _windowType: WindowTypeModel
     */
    updateWindowType(_model: WindowType) {
        this.loadingAfterSubmit = true;
        this.viewLoading = true;

        const updateWindowType: Update<WindowType> = {
            id: _model.id,
            changes: _model
        };
        this.store.dispatch(new WindowTypeUpdated({
            partialWindowType: updateWindowType,
            windowType: _model
        }));
        this.oldModel = _model;

        this.navigateToParent();

    }

    /**
     * Create model
     *
     * @param _model: WindowTypeModel
     */
    createWindowType(_model: WindowType) {
        this.store.dispatch(new WindowTypeOnServerCreated({windowType: _model}));
        this.oldModel = _model;
        this.componentSubscriptions = this.store.pipe(
            select(selectLastCreatedWindowTypeId),
            delay(1000), // Remove this line
        ).subscribe(res => {
            if (!res) {
                return;
            }
            this.navigateToParent();
        });
    }

    /** Alect Close event */
    onAlertClose($event) {
        this.hasFormErrors = false;
    }


    isFormValid() {
        return (this.model && this.model.name && this.model.name.length > 0);
    }

    navigateToParent() {
        let url = '../';
        if (this.model.id > 0) {
            url = '../../';
        }
        this.router.navigate([url], {relativeTo: this.activatedRoute});
    }

    canDeactivate() {
        if (this.discard()) {
            if (window.confirm('Are you sure? All unsaved changes will be lost.')) {
                return true;
            } else {
                // ---------work around angular bug--------- reference: https://github.com/angular/angular/issues/13586
                const currentUrlTree = this.router.createUrlTree([], this.activatedRoute.snapshot);
                const currentUrl = currentUrlTree.toString();
                this.location.go(currentUrl);
                // ---------work around end-----------------
                return false;
            }
        }
        return true;
    }

    discard() {
        if (this.model && this.oldModel) {
            return this.model.name != this.oldModel.name;
        }
        return false;
    }

}
